'use client'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Pin } from 'lucide-react'
import * as React from 'react'

import { toast } from '@/hooks/use-toast'

import PostMemeCard from '@/components/meme/post-meme-card'

import { useAuth } from '@/contexts/auth-context'
import memeService from '@/services/meme.service'
import { isAdmin, isInstructor } from '@/utils/admin-utils'

import { EmojiData, PinnedPost } from '@/types/meme.interface'

interface PinnedPostsSectionProps {
  channelId: string
  onToggleReaction?: (postId: string, reactionType?: string, emojiData?: EmojiData) => Promise<void>
}

export default function PinnedPostsSection({ channelId, onToggleReaction }: PinnedPostsSectionProps) {
  const queryClient = useQueryClient()
  const { user, isAuthenticated } = useAuth()
  const canPin = isAdmin(user) || isInstructor(user)

  const { data: pinnedPosts } = useQuery({
    queryKey: ['pinned-posts', channelId],
    queryFn: () => memeService.getPinnedPosts(channelId),
    enabled: !!channelId,
  })

  const handleUnpin = React.useCallback(
    async (postId: string) => {
      try {
        await memeService.unpinPost(channelId, postId)
        queryClient.invalidateQueries({ queryKey: ['pinned-posts', channelId] })
        toast({ title: 'Thành công', description: 'Đã bỏ ghim bài viết' })
      } catch {
        toast({ title: 'Lỗi', description: 'Không thể bỏ ghim bài viết', variant: 'destructive' })
      }
    },
    [channelId, queryClient]
  )

  if (!pinnedPosts || pinnedPosts.length === 0) return null

  return (
    <div className="space-y-4">
      {pinnedPosts.map((pinned: PinnedPost) => (
        <div key={pinned.id} className="relative">
          <div className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-b-0 border-amber-200 rounded-t-lg">
            <Pin className="h-3 w-3" />
            <span>Đã ghim</span>
          </div>
          <div className="border-l-2 border-amber-400 rounded-bl-none">
            <PostMemeCard
              post={pinned.post}
              onToggleReaction={onToggleReaction}
              isAuthenticated={isAuthenticated}
              isPinned
              onUnpin={canPin ? handleUnpin : undefined}
            />
          </div>
        </div>
      ))}
    </div>
  )
}
